import { Button, MultiSelect, Stack } from "@mantine/core";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { httpPostGraphql, StrictUndef } from "shared/api/httpClient";
import { handleRoleAssign, UserSchema } from "shared/components/organization/users/data";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { logger } from "shared/utils/logger";
import { graphql } from "src/gql";
import { FullUserFragment } from "src/gql/graphql";

export const IpawsRoleManage = ({ userIds, selectedRoleId }: { userIds: string[]; selectedRoleId: string }) => {
    const assignIpawsRole = useGraphqlMutation(muts.assignIpawsRole);
    const ipawsCerts = useSuspenseQuery(queries.getCerts);

    const [selectedCerts, setSelectedCerts] = useState<string[]>([]);

    return (
        <Stack>
            <MultiSelect
                label="Select certificate"
                placeholder="Pick a certificate..."
                data={ipawsCerts.data.ipawsCertificates.map((it) => ({ label: it.name, value: it.id }))}
                onChange={(it) => setSelectedCerts(it)}
            />
            <Button
                w="100%"
                disabled={selectedCerts.length == 0}
                onClick={() => {
                    handleRoleAssign(
                        userIds.flatMap((id) =>
                            selectedCerts.map((cert) =>
                                assignIpawsRole
                                    .mutateAsync({ userId: id, roleId: selectedRoleId, ipawsCertId: cert })
                                    .then((res) => res.assignIpawsRole.user as StrictUndef<FullUserFragment>),
                            ),
                        ),
                    ).catch(logger.error);
                }}
            >
                Save
            </Button>
        </Stack>
    );
};

export const IpawsEditPanel = ({
    user,
    selectedRoleId,
    initialCertIds,
}: {
    user: UserSchema;
    selectedRoleId: string;
    initialCertIds: string[];
}) => {
    const ipawsCerts = useSuspenseQuery(queries.getCerts);
    const [selectedCerts, setSelectedCerts] = useState(initialCertIds);

    const assignIpawsRole = useGraphqlMutation(muts.assignIpawsRole);
    const unassignRole = useGraphqlMutation(muts.unassignRole);

    const hasDiff =
        selectedCerts.length != initialCertIds.length ||
        !selectedCerts.every((selectedId) => initialCertIds.some((initialId) => initialId == selectedId));

    return (
        <Stack>
            <MultiSelect
                placeholder="Pick a certificate..."
                defaultValue={initialCertIds}
                data={ipawsCerts.data.ipawsCertificates.map((it) => ({ label: it.name, value: it.id }))}
                onChange={(it) => setSelectedCerts(it)}
            />
            <Button
                disabled={!hasDiff}
                onClick={() => {
                    const addedCerts = selectedCerts.filter((selectedId) => !initialCertIds.some((initialId) => selectedId == initialId));
                    const removedCerts = initialCertIds.filter((initialId) => !selectedCerts.some((selectedId) => initialId == selectedId));

                    const promises = [
                        ...addedCerts.map((id) =>
                            assignIpawsRole
                                .mutateAsync({ userId: user.id, roleId: selectedRoleId, ipawsCertId: id })
                                .then((res) => res.assignIpawsRole.user as StrictUndef<FullUserFragment>),
                        ),
                        ...removedCerts.map((id) => {
                            const userRoleId = user.roles.find((r) => r.role.id == selectedRoleId && r.ipawsCert?.id == id)?.id;
                            if (!userRoleId)
                                return Promise.reject(
                                    `userRoleId not found on user ${user.id} for role ${selectedRoleId} for ipaws cert ${id}`,
                                );
                            return unassignRole
                                .mutateAsync({ userId: user.id, userRoleId })
                                .then((res) => res.unassignRole as StrictUndef<FullUserFragment>);
                        }),
                    ];

                    handleRoleAssign(promises).catch(logger.error);
                }}
            >
                Save
            </Button>
        </Stack>
    );
};

const queries = {
    getCerts: queryOptions({
        queryKey: ["roleAssign", "ipaws", "certificates"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query IpawsRoleManageCertQuery {
                        ipawsCertificates {
                            id
                            name
                        }
                    }
                `),
                {},
            ),
    }),
};

const muts = {
    assignIpawsRole: graphql(`
        mutation IpawsRoleManageAssignIpawsRole($userId: UUID!, $roleId: UUID!, $ipawsCertId: UUID!) {
            assignIpawsRole(params: { roleId: $roleId, userId: $userId, ipawsCertId: $ipawsCertId }) {
                user {
                    ...FullUser
                }
            }
        }
    `),
    unassignRole: graphql(`
        mutation IpawsRoleManageUnassignRole($userId: UUID!, $userRoleId: UUID!) {
            unassignRole(userId: $userId, userRoleId: $userRoleId) {
                ...FullUser
            }
        }
    `),
};
